/**
 * Aggregate data for the owner (lessor) detail modal.
 *
 * From a single `lessors` row this composes everything the 6-tab modal needs:
 *  - Apartments linked by EFFECTIVE lessor, i.e.
 *    COALESCE(apartments.lessor_id, properties.lessor_id) (+ room counts)
 *  - Management-fee invoices (management_invoices by lessor_id)
 *  - EVERY onboarding submission attributable to the owner, each with its own
 *    signed documents (owner_onboarding_submissions + owner_signed_contracts)
 *  - Manually uploaded documents (owner_documents by lessor_id)
 *  - SaaS subscription (saas_subscriptions by lessor_id)
 *  - Derived Overview stats (portfolio size, fees paid YTD, revenue model)
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Lessor } from "@/types/lessor";
import type { RevenueModel, CollectionMode } from "@/types/property";
import { isPaidCurrentPeriod } from "@/lib/rent-reminder-templates";
import { isActiveBooking, roomOccupancyPhase } from "@/hooks/v2/useProperties";
import { currentMonthKey } from "@/hooks/v2/useApartmentMonthRent";
import { fetchAllByColumn } from "@/hooks/v2/usePropertyRentPaidStats";
import { nextMonthStart } from "@/lib/payment-booking";
import {
  computeCarriedOverPayoutGroups,
  computeOwnerPayoutGroups,
  countReadyPayoutGroups,
  groupPaymentsByDueMonth,
  openOffsetPropertiesByMonth,
  type PayoutApartmentInput,
  type PayoutRoomRow,
  type PayoutBookingRow,
  type PayoutPaymentRow,
  type OwnerPayoutGroup,
} from "@/lib/owner-payouts";
import { resolveCoverImage } from "@/lib/v2-images";
import { fetchLessorIdByOwnerUser } from "@/hooks/v2/owner-user-lessor";
import { logOwnerAudit, OWNER_AUDIT_SOURCE } from "@/lib/owner-audit";
import {
  OWNER_DETAIL_KEY as ownerDetailKey,
  OWNER_AUDIT_KEY as ownerAuditKey,
} from "./owner-detail-keys";
import type { QueryPollOptions } from "@/lib/query-poll";
import {
  type OnboardingStage,
  type UnitFeeConfig,
  ONBOARDING_STAGE_LABEL,
  ownerPropertyOnboardingStage,
  lowestOnboardingStage,
  type OnboardingTokenWithSubmissions,
  normalizeOwnerEmail,
  worstInvoiceStatus,
  effectiveApartmentOwner,
  roomBelongsToOwner,
  resolveUnitFeeConfig,
  unitMonthlyRevenue,
} from "@/lib/owner-metrics";

/** Storage bucket shared with signed contracts + management-fee proofs. */
const DOC_BUCKET = "contracts";

// Onboarding-stage helpers now live in the pure, testable owner-metrics module.
// Re-exported here so existing importers (OwnersTable, LessorsListPage) don't
// need to change their import path.
export {
  ONBOARDING_STAGE_LABEL,
  ONBOARDING_STAGE_RANK,
  type OnboardingStage,
} from "@/lib/owner-metrics";

/**
 * Surface (never swallow) a failed *enrichment* query without letting it erase
 * the whole owner detail. Core queries (properties, invoices) still throw — the
 * modal is meaningless without them — but a failure while decorating properties
 * with onboarding stage / room counts / current tenants should degrade to "that
 * decoration is missing", not "the owner has no properties". This also keeps the
 * modal working across environments whose schema has drifted (e.g. a dev project
 * missing a column the LIVE project has).
 */
function warnEnrichment(scope: string, error: unknown): void {
  if (error)
    console.warn(
      `[useOwnerDetail] ${scope} enrichment failed (owner detail degraded, not broken):`,
      error
    );
}

/**
 * Onboarding tokens for a set of properties, apartment-aware when the schema
 * allows it.
 *
 * `apartment_id` only exists after migration 20260803090000. Selecting it
 * against a database that predates that migration errors the WHOLE query, and
 * because the stage derivation degrades to "pending" on failure, every owner
 * would suddenly read "Onboarding pending". So try the apartment-aware select
 * first and fall back to the property-only shape, where every token counts as
 * property-wide — exactly the behaviour before apartment-scoped invitations.
 */
async function fetchOnboardingTokens(
  propertyIds: number[]
): Promise<{ rows: any[]; error: unknown }> {
  const withApartment = await supabase
    .from("owner_onboarding_tokens")
    .select("id, property_id, email, apartment_id")
    .in("property_id", propertyIds);
  if (!withApartment.error) {
    return { rows: withApartment.data ?? [], error: null };
  }
  const legacy = await supabase
    .from("owner_onboarding_tokens")
    .select("id, property_id, email")
    .in("property_id", propertyIds);
  return { rows: legacy.data ?? [], error: legacy.error };
}

/**
 * Exact, case-insensitive email match — the check that must gate anything shown
 * under an owner.
 *
 * `.ilike("email", address)` alone is NOT that check: `_` and `%` are wildcards
 * in a LIKE pattern, so `jan_mueller@web.de` also matches `janamueller@web.de`
 * and one owner's masked IBAN, BIC and signed contracts would render in
 * another's modal. PostgREST can't express `lower(email) = ?`, so `ilike` stays
 * the coarse server-side narrowing and this decides what actually belongs to
 * the owner — the same rule useOwnersList applies in memory.
 */
function sameOwnerEmail(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const left = normalizeOwnerEmail(a);
  return left != null && left === normalizeOwnerEmail(b);
}

/** A tenant currently occupying a room in one of the owner's apartments. */
export type OwnerPropertyTenant = {
  bookingId: number;
  roomId: number;
  roomTitle: string | null;
  name: string;
  email: string | null;
  /** Current-period rent state, for an at-a-glance badge on the tenant row. */
  rentStatus: "paid" | "overdue" | "pending";
};

/** Property context an apartment is shown in and inherits its fee config from. */
type OwnerPropertyMeta = {
  id: number;
  title: string | null;
  city: string | null;
  /** Postal code — shown with the town, never on its own. */
  zip_code: string | null;
  address: string | null;
  cover_image: string | null;
  /** Property-level fee config — the fallback an apartment inherits. */
  config: UnitFeeConfig;
  collection_mode: CollectionMode | null;
};

/**
 * One billable unit in the owner's portfolio. Normally an apartment; when a
 * property still has rooms that aren't assigned to any apartment, those rooms
 * form one property-level unit (`unassigned: true`) so their rooms and revenue
 * aren't silently dropped from the owner's totals. This mirrors the DB
 * generator, which also bills apartment-level units and an `apartment_id IS
 * NULL` remainder per property.
 */
export type OwnerApartment = {
  /** apartments.id — null for the property-level (unassigned rooms) unit. */
  id: number | null;
  /** Apartment label, or a stand-in for the unassigned-rooms unit. */
  label: string;
  floor: string | null;
  unassigned: boolean;
  property_id: number;
  property_title: string | null;
  property_city: string | null;
  /** Postal code of the property — rendered alongside the city. */
  property_zip: string | null;
  property_address: string | null;
  /** The property's cover image — apartments don't carry their own. */
  cover_image: string | null;
  rooms_count: number;
  /** This unit's own onboarding lifecycle (its invitation, else a property-wide one). */
  onboarding_stage: OnboardingStage;
  onboarding_label: string;
  /** Tenants currently living in this apartment (active, moved in). */
  current_tenants: OwnerPropertyTenant[];
  // EFFECTIVE revenue model — the apartment's own, else the property's.
  revenue_model: RevenueModel | null;
  revenue_model_label: string;
  collection_mode: CollectionMode | null;
  collection_label: string;
  coliviq_fee_pct: number | null;
  /** Flat management fee per room in EUR (revenue_model = flat_fee_per_room). */
  coliviq_fee_amount: number | null;
  master_rent: number | null;
  /** Summed rent of occupied rooms — the base for pct_of_rent / full_rent. */
  occupied_rent: number;
  /** CoLiviq's computed monthly revenue from this apartment. */
  monthly_revenue: number;
  /**
   * Rooms `generate_management_invoices` would count as occupied for this
   * unit — `rooms.status = 'occupied' OR rooms.available = false`.
   *
   * Deliberately NOT the same reading as occupied_rent / current_tenants,
   * which come from bookings because the room flags aren't reliably maintained
   * (see the occupancy comment below). Both readings are kept because they
   * answer different questions: bookings say who is actually living there,
   * the room flags say what the BILLING generator saw. Explaining why a
   * property earned no management fee needs the generator's reading — the
   * booking one would explain a decision the generator never made.
   */
  generator_occupied_rooms: number;
  /**
   * Summed price of the rooms counted in generator_occupied_rooms — the
   * GENERATOR's own occupied-rent reading, paired with generator_occupied_rooms
   * so a caller can run the exact same fee math (unitMonthlyRevenue) the
   * generator runs, rather than the booking-derived occupied_rent above.
   */
  generator_occupied_rent: number;
  /**
   * True when the rooms read failed. Like revenue_degraded, this marks the 0
   * above as unknown rather than a verified vacancy — nothing may claim "no
   * occupied rooms" on the strength of a query that did not run.
   */
  rooms_degraded: boolean;
  /**
   * True when the bookings query that occupied_rent/monthly_revenue/
   * current_tenants derive from failed outright (both the full select and its
   * rent_paid_through-less fallback). In that case the 0s above are NOT a real
   * "vacant" reading — render them as unknown ("—"), not €0.00, so a degraded
   * query can't masquerade as a verified zero.
   */
  revenue_degraded: boolean;
};

export type OwnerInvoice = {
  id: string;
  invoice_no: string | null;
  period_month: string;
  amount_cents: number;
  status: string;
  due_date: string | null;
  paid_at: string | null;
  proof_url: string | null;
  invoice_pdf_url: string | null;
  fee_basis: Record<string, unknown> | null;
  /** Raw invoice metadata — carries proof_note/proof_note_at for the paid gate. */
  metadata: Record<string, unknown> | null;
  /** Operator note captured when the payment proof was uploaded (from metadata). */
  proof_note: string | null;
};

export type OwnerSubmission = {
  id: string;
  reference_number: string | null;
  submitted_at: string | null;
  status: string;
  rejection_reason: string | null;
  address: string | null;
  bank_holder: string | null;
  iban: string | null;
  bic: string | null;
  payment_method: string | null;
  management_agreement_accepted: boolean;
  verwaltervollmacht_accepted: boolean;
  agb_accepted: boolean;
  withdrawal_policy_accepted: boolean;
  verified_at: string | null;
  supabase_user_id: string | null;
  propertyTitle: string | null;
  propertyAddress: string | null;
  feeAmount: number | null;
  feeModel: string | null;
  /**
   * Contracts signed through THIS submission. Per submission, not per owner: an
   * owner who onboarded twice has two sets, and the Onboarding tab nests each
   * under its own block.
   */
  signedContracts: { name: string; url: string }[];
};

export type OwnerDoc = {
  id: string;
  title: string;
  doc_type: string;
  file_url: string;
  file_name: string | null;
  file_size: number | null;
  notes: string | null;
  created_at: string;
};

export type OwnerSubscription = {
  id: string;
  plan: string;
  status: string;
  amount_cents: number;
  billing_day: number | null;
  started_at: string | null;
} | null;

export type OwnerDetail = {
  /** The owner's apartments (effective lessor), ordered by property then label. */
  apartments: OwnerApartment[];
  /**
   * Property-level units: rooms in the owner's properties that aren't assigned
   * to any apartment. Usually empty; listed separately so they stay visible
   * without being counted as apartments.
   */
  unassignedUnits: OwnerApartment[];
  invoices: OwnerInvoice[];
  /**
   * Every onboarding submission attributable to this owner, newest first — an
   * owner can onboard more than once (one per apartment/property, or a
   * re-submission after a rejection). The Onboarding tab nests them all.
   */
  submissions: OwnerSubmission[];
  /** The latest submission — what the stats, documents and activity read. */
  submission: OwnerSubmission | null;
  signedDocs: { name: string; url: string }[];
  ownerDocs: OwnerDoc[];
  subscription: OwnerSubscription;
  stats: {
    /** Apartments linked to the owner — the Overview "Apartments" tile. */
    apartmentCount: number;
    /** Properties those apartments sit in (context, not a headline KPI). */
    propertyCount: number;
    roomCount: number;
    feesPaidThisYear: number;
    /** Sum of every unit's computed monthly CoLiviq revenue. */
    totalMonthlyFees: number;
    currentMonthlyFee: number | null;
    revenueModelLabel: string;
    rentCollectionLabel: string;
    /**
     * Management fee the admin set for this owner in the Add/Edit-account flow
     * (profiles.management_fee, in € per room per month). This is the value the
     * "Assign to properties → Management fee (€ / Zimmer / Monat)" input writes,
     * so surfacing it here keeps the owner detail in sync with what was entered.
     */
    managementFeePerRoom: number | null;
    /** Room count the fee was quoted against (profiles.management_fee_rooms), if set. */
    managementFeeRooms: number | null;
    /** True when totalMonthlyFees is degraded (see OwnerApartment.revenue_degraded). */
    revenueDegraded: boolean;
  };
};

// Keys live in their own module (owner-detail-keys) so invalidators don't have
// to import this hook. Re-exported here for existing importers.
export { OWNER_DETAIL_PREFIX } from "./owner-detail-keys";
export const OWNER_DETAIL_KEY = ownerDetailKey;

/** Human label for a revenue model from an invoice fee_basis / onboarding token. */
function revenueModelLabel(
  feeBasis: Record<string, unknown> | null | undefined,
  feeModel: string | null | undefined,
  feeAmount: number | null | undefined
): string {
  const basis = feeBasis || {};
  const pct =
    (basis.fee_percent as number | undefined) ??
    (basis.percentage as number | undefined) ??
    (feeModel === "percentage" || feeModel === "percent"
      ? (feeAmount ?? undefined)
      : undefined);
  const fixed =
    (basis.fee_amount as number | undefined) ??
    (feeModel === "fixed" ? (feeAmount ?? undefined) : undefined);
  if (typeof pct === "number" && pct > 0) return `${pct}% of rent`;
  if (typeof fixed === "number" && fixed > 0) return `Fixed €${fixed} / month`;
  return "—";
}

/** "CoLiviq collects rent" vs "Owner collects rent" from fee_basis.revenue_model. */
function rentCollectionLabel(
  feeBasis: Record<string, unknown> | null | undefined
): string {
  const rm = String((feeBasis || {}).revenue_model ?? "").toLowerCase();
  if (rm.includes("saas") || rm.includes("self")) return "Self-managed (SaaS)";
  if (rm.includes("collect") && rm.includes("owner"))
    return "Owner collects rent";
  if (rm) return "coliviq collects rent";
  return "—";
}

/** Human label for a property's revenue model enum. */
function propertyRevenueModelLabel(model: RevenueModel | null): string {
  switch (model) {
    case "flat_fee_per_room":
      return "Flat fee per room";
    case "pct_of_rent":
      return "Percentage of rent";
    case "full_rent":
      return "Full rent";
    default:
      return "—";
  }
}

/** Human label for a property's collection mode. */
function propertyCollectionLabel(mode: CollectionMode | null): string {
  if (mode === "owner_collects") return "Owner collects rent";
  if (mode === "company_collects") return "coliviq collects rent";
  return "—";
}

/** One property under an owner, with its onboarding stage. */
export type OwnerListProperty = {
  id: number;
  title: string | null;
  city: string | null;
  onboardingStage: OnboardingStage;
  /** Held directly (properties.lessor_id = this owner), not only via apartments. */
  heldDirectly?: boolean;
};

/** One apartment under an owner, with the property it sits in (row expand). */
export type OwnerListApartment = {
  id: number;
  label: string;
  propertyId: number;
  propertyTitle: string | null;
  propertyCity: string | null;
  /** Stage of the apartment's PROPERTY — onboarding tokens are property-scoped. */
  onboardingStage: OnboardingStage;
  /**
   * Owned through the apartment itself (its own lessor_id, or a property this
   * owner doesn't hold), not inherited from the property's lessor.
   */
  apartmentScoped?: boolean;
};

/** Enriched owner row for the columnar Owners list. */
export type OwnerListRow = Lessor & {
  /**
   * Apartments linked to this owner by EFFECTIVE lessor —
   * COALESCE(apartments.lessor_id, properties.lessor_id), the same rule
   * generate_management_invoices bills by. Drives the Apartments column.
   */
  apartmentsCount: number;
  /** Per-apartment breakdown, for the expandable row. */
  apartmentList: OwnerListApartment[];
  /**
   * Properties held DIRECTLY (properties.lessor_id) — only the delete warning
   * uses this, since those are the rows whose lessor field goes blank. Usually
   * smaller than `propertyList` and often 0: most owners hold apartments only.
   */
  propertiesCount: number;
  /** Worst current-month invoice status across the owner's invoices. */
  currentInvoiceStatus: string | null;
  /**
   * Number of property-level payout groups fully collected and not yet paid
   * out THIS month (COL-461) — see computeOwnerPayoutGroups in
   * src/lib/owner-payouts.ts. The "Payouts ready" stat tile adds the ready
   * ones in `carriedOverPayoutGroups` to it (COL-515).
   */
  readyPayoutGroups: number;
  /** Every payout group behind that count, with its status and live payout —
   *  the rows of the payouts view (COL-474). Empty on a degraded read. */
  payoutGroups: OwnerPayoutGroup[];
  /**
   * Payouts from EARLIER months that are still Pending or Ready (COL-515): the
   * same groups as `payoutGroups`, computed for a month that has an unpaid offset
   * invoice left, oldest month first. Completed ones are not listed. Empty on a
   * degraded read, like `payoutGroups`.
   */
  carriedOverPayoutGroups: OwnerPayoutGroup[];
  /** A read feeding the payouts failed on this fetch, so `payoutGroups` is
   *  empty because it is UNKNOWN, not because nothing is owed. The payouts
   *  view shows an error instead of "no payouts" (COL-474). Set per fetch, so
   *  every row carries the same value. */
  payoutDataDegraded: boolean;
  /** Aggregate onboarding stage = the LOWEST (least-progressed) across `propertyList`. */
  onboardingStage: OnboardingStage | null;
  /**
   * EVERY property the owner has a stake in — held directly, or hosting one of
   * their apartments. Onboarding tokens hang off a property (optionally narrowed
   * to one apartment), so this is the set the Status pill aggregates over;
   * scoping it to directly-held properties left apartment-only owners
   * permanently blank.
   */
  propertyList: OwnerListProperty[];
};

/** Raw apartment shape the list query reads (before owner resolution). */
type ListAptRow = {
  id: number | string;
  label: string | null;
  property_id: number | string;
  lessor_id: string | null;
  /** Owner ACCOUNT link — resolved to a lessor via fetchLessorIdByOwnerUser. */
  owner_user_id?: string | null;
  /** NULL = inherit the property's collection_mode (COL-461 payout readiness). */
  collection_mode?: CollectionMode | null;
  /** Fee config override; NULL revenue_model = inherit the property's whole
   *  config (resolveUnitFeeConfig). Decides whether the unit bills anything,
   *  which decides whether a payout can be recorded for it at all. */
  revenue_model?: UnitFeeConfig["revenue_model"];
  coliviq_fee_pct?: number | string | null;
  coliviq_fee_amount?: number | string | null;
  master_rent?: number | string | null;
};

/** PostgREST hands numerics back as strings often enough that every read of
 *  these columns has to normalise; one place to do it. */
function feeConfigFromRow(r: {
  revenue_model?: UnitFeeConfig["revenue_model"];
  coliviq_fee_pct?: number | string | null;
  coliviq_fee_amount?: number | string | null;
  master_rent?: number | string | null;
}): UnitFeeConfig {
  const num = (v: number | string | null | undefined) =>
    v != null ? Number(v) : null;
  return {
    revenue_model: r.revenue_model ?? null,
    coliviq_fee_pct: num(r.coliviq_fee_pct),
    coliviq_fee_amount: num(r.coliviq_fee_amount),
    master_rent: num(r.master_rent),
  };
}

/**
 * generate_management_invoices defaults a unit with no revenue_model
 * configured anywhere (apartment nor property) to 'full_rent'
 * (COALESCE(a.revenue_model, p.revenue_model, 'full_rent')) rather than
 * billing it nothing — resolveUnitFeeConfig alone does not apply that
 * default, so payout readiness must apply it on top, or such a unit reads as
 * unbillable here while the generator bills it.
 */
function withGeneratorFeeDefault(config: UnitFeeConfig): UnitFeeConfig {
  return {
    ...config,
    revenue_model: config.revenue_model ?? "full_rent",
  };
}

/**
 * Owners list with the per-owner enrichment the columnar table shows:
 * apartment count + per-apartment list, property count, worst current-month
 * invoice status, and the aggregate (lowest) onboarding stage across the
 * owner's properties.
 */
export function useOwnersList(
  search?: string,
  enabled = true,
  poll?: QueryPollOptions
) {
  return useQuery({
    queryKey: ["v2", "owners-list", search ?? ""] as const,
    enabled,
    ...poll,
    queryFn: async (): Promise<OwnerListRow[]> => {
      // Newest owner record first — the list reads as "what changed lately"
      // rather than an alphabet. `name` breaks ties so two owners created in the
      // same transaction (bulk import) still land in a stable order.
      let q = supabase
        .from("lessors")
        .select("*")
        .order("created_at", { ascending: false })
        .order("name", { ascending: true });
      const term = (search ?? "").trim();
      if (term) {
        // Strip characters that break PostgREST's .or() grammar (commas,
        // parens, dots) before interpolating — an unescaped `,` or `(` makes
        // the whole filter unparseable and errors the entire owners list.
        const safeTerm = term
          .replace(/[,()]/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        if (safeTerm)
          q = q.or(`name.ilike.%${safeTerm}%,email.ilike.%${safeTerm}%`);
      }
      const { data: lessors, error } = await q;
      if (error) throw error;
      const rows = (lessors ?? []) as Lessor[];
      if (rows.length === 0) return [];

      const ids = rows.map((l) => l.id);
      const now = new Date();
      const ymKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

      // Every read below feeds the COL-461 payout-readiness decision, so a
      // silently truncated response is a wrong money answer rather than a
      // cosmetic gap: an apartment dropped at PostgREST's per-response row cap
      // never joins its group, so its tenancy's rent leaves the group's
      // expected total and the group reads FULLY COLLECTED — "ready" for a
      // payout on rent nobody collected. A bare `.in()` hits that cap without
      // erroring, so these go through the same `fetchAllByColumn` paging the
      // payout-specific reads further down already use. The wrapper keeps the
      // `{ data, error }` shape so each caller below still chooses for itself
      // whether to throw or degrade via warnEnrichment.
      const pagedByColumn = async <Row>(
        table: string,
        select: string,
        column: string,
        values: Array<number | string>,
        orderColumn: string,
        ascending: boolean,
        extraFilter?: (query: any) => any
      ): Promise<{ data: Row[] | null; error: unknown }> => {
        try {
          return {
            data: await fetchAllByColumn<Row>(
              table,
              select,
              column,
              values,
              orderColumn,
              ascending,
              extraFilter
            ),
            error: null,
          };
        } catch (error) {
          return { data: null, error };
        }
      };

      const [propsRes, directAptRes, invRes, lessorByOwnerUser] =
        await Promise.all([
          pagedByColumn<any>(
            "properties",
            "id, lessor_id, title, city, collection_mode, revenue_model, coliviq_fee_pct, coliviq_fee_amount, master_rent",
            "lessor_id",
            ids,
            "id",
            true
          ),
          // Apartments assigned directly to one of these owners. These may sit
          // inside a property held by a DIFFERENT lessor, so they can't be
          // reached through the properties query above.
          pagedByColumn<ListAptRow>(
            "apartments",
            "id, label, property_id, lessor_id, owner_user_id, collection_mode, revenue_model, coliviq_fee_pct, coliviq_fee_amount, master_rent",
            "lessor_id",
            ids,
            "id",
            true
          ),
          pagedByColumn<any>(
            "management_invoices",
            // id: how a payout group names the invoices its live payout is
            // still waiting on (OwnerPayoutGroup.unpaidInvoiceIds, COL-516).
            // Record payout compares that set against its own invoice list by
            // identity before it saves a figure, so dropping this column does
            // not just lose a field — it silently stops the action offering
            // any amount at all.
            // apartment_id: NULL on every row the current generator writes, so
            // a row carrying one is a legacy per-apartment document — which is
            // how payout readiness tells the two apart (owner-payouts.ts).
            // amount_cents/sent_at: the live offset fee and whether it is
            // frozen, for the payouts view (COL-474). invoice_no and the
            // fee_basis figures: which slice of the month's rent each invoice
            // pays out once a month has a remainder (COL-483), and whether it
            // is a reissue replacement, which is never the month's open
            // invoice. Read as JSON paths rather than the new remainder
            // columns: a missing key is just NULL, while an unknown column
            // fails the whole read, so the owners list keeps working whichever
            // of app and database deploys first.
            "id, lessor_id, property_id, apartment_id, period_month, status, due_date, settlement, amount_cents, sent_at, paid_at, invoice_no, remainder_of_invoice_no:fee_basis->>remainder_of_invoice_no, collected_rent_eur:fee_basis->collected_rent_eur, prior_collected_rent_eur:fee_basis->prior_collected_rent_eur, collected_since_close_eur:fee_basis->collected_since_close_eur, rent_outstanding:fee_basis->rent_outstanding, replaces_invoice_id:metadata->>replaces_invoice_id",
            "lessor_id",
            ids,
            "period_month",
            false
          ),
          fetchLessorIdByOwnerUser(),
        ]);
      if (propsRes.error) throw propsRes.error;
      if (invRes.error) throw invRes.error;
      // Set by ANY read the payout tile depends on failing — see the long
      // note at the payout reads below for why a partial dataset must never
      // be computed on. Declared up here because the apartment/property
      // enrichments above the payout reads feed it too: they decide which
      // units exist at all, and a silent [] from one of them removes an
      // unpaid apartment from its group rather than erroring, which reads as
      // "nothing outstanding" instead of "unknown".
      let payoutDataDegraded = false;

      // Apartments only decorate the list — degrade to "no apartments known"
      // rather than blanking the whole owners table (same policy as the other
      // enrichments; see warnEnrichment). The payout tile is the exception:
      // it is suppressed rather than computed on what survived.
      warnEnrichment("owners-list apartments (direct)", directAptRes.error);
      if (directAptRes.error) payoutDataDegraded = true;

      const idSet0 = new Set(ids.map(String));

      // Properties grouped per lessor (+ collect ids for the token lookup).
      // Keys are stringified: properties.id and apartments.property_id are both
      // bigint, and PostgREST doesn't narrow them to JS numbers consistently
      // across queries, so a Map<number, …> can silently miss every match.
      const propsByLessor = new Map<
        string,
        { id: number; title: string | null; city: string | null }[]
      >();
      const propMetaById = new Map<
        string,
        {
          title: string | null;
          city: string | null;
          collectionMode: CollectionMode;
          feeConfig: UnitFeeConfig;
        }
      >();
      const lessorByProp = new Map<string, string>();
      const propIds: number[] = [];
      for (const p of propsRes.data ?? []) {
        if (!p.lessor_id) continue;
        propIds.push(Number(p.id));
        propMetaById.set(String(p.id), {
          title: p.title,
          city: p.city,
          collectionMode: p.collection_mode ?? "company_collects",
          feeConfig: feeConfigFromRow(p),
        });
        lessorByProp.set(String(p.id), p.lessor_id);
        const arr = propsByLessor.get(p.lessor_id) ?? [];
        arr.push({ id: Number(p.id), title: p.title, city: p.city });
        propsByLessor.set(p.lessor_id, arr);
      }

      // Apartments that INHERIT their owner from the property (lessor_id NULL).
      let inheritedApts: ListAptRow[] = [];
      if (propIds.length > 0) {
        const { data, error } = await pagedByColumn<ListAptRow>(
          "apartments",
          "id, label, property_id, lessor_id, owner_user_id, collection_mode, revenue_model, coliviq_fee_pct, coliviq_fee_amount, master_rent",
          "property_id",
          propIds,
          "id",
          true,
          (q: any) => q.is("lessor_id", null)
        );
        warnEnrichment("owners-list apartments (inherited)", error);
        if (error) payoutDataDegraded = true;
        inheritedApts = (data ?? []) as ListAptRow[];
      }
      // Apartments owned through an owner ACCOUNT (owner_user_id) rather than a
      // lessor. These sit in properties nobody on this list holds — the two
      // queries above can't see them, and before this they belonged to no one.
      const ownerUserIds = Object.entries(lessorByOwnerUser)
        .filter(([, lessorId]) => idSet0.has(String(lessorId)))
        .map(([userId]) => userId);
      let accountApts: ListAptRow[] = [];
      if (ownerUserIds.length > 0) {
        const { data, error } = await pagedByColumn<ListAptRow>(
          "apartments",
          "id, label, property_id, lessor_id, owner_user_id, collection_mode, revenue_model, coliviq_fee_pct, coliviq_fee_amount, master_rent",
          "owner_user_id",
          ownerUserIds,
          "id",
          true,
          (q: any) => q.is("lessor_id", null)
        );
        warnEnrichment("owners-list apartments (owner account)", error);
        if (error) payoutDataDegraded = true;
        accountApts = (data ?? []) as ListAptRow[];
      }
      // Direct rows have a lessor_id; the other two don't. Dedupe by id, since
      // an inherited apartment can also name an owner account.
      const aptById = new Map<string, ListAptRow>();
      for (const a of [
        ...((directAptRes.data ?? []) as ListAptRow[]),
        ...inheritedApts,
        ...accountApts,
      ]) {
        aptById.set(String(a.id), a);
      }
      const aptRows: ListAptRow[] = Array.from(aptById.values());

      // Effective lessor per apartment: own lessor → owner account's lessor →
      // the property's.
      const idSet = idSet0;
      const aptsByLessor = new Map<string, ListAptRow[]>();
      for (const a of aptRows) {
        const owner = effectiveApartmentOwner(
          a.lessor_id,
          lessorByProp.get(String(a.property_id)),
          a.owner_user_id ? lessorByOwnerUser[a.owner_user_id] : null
        );
        if (!owner || !idSet.has(String(owner))) continue;
        const arr = aptsByLessor.get(String(owner)) ?? [];
        arr.push(a);
        aptsByLessor.set(String(owner), arr);
      }

      // COL-461 payout attribution must match generate_management_invoices's
      // own billed-party rule — COALESCE(apartment.lessor_id, property.lessor_id)
      // — NOT the owner-ACCOUNT fallback effectiveApartmentOwner adds above for
      // the apartment-count/onboarding-stage display. An apartment whose
      // lessor_id is cleared but whose owner_user_id still resolves to a
      // different owner would otherwise show a payout "ready" on an owner's row
      // the generator never actually bills for that unit.
      const payoutAptsByLessor = new Map<string, ListAptRow[]>();
      for (const a of aptRows) {
        const owner = a.lessor_id ?? lessorByProp.get(String(a.property_id));
        if (!owner || !idSet.has(String(owner))) continue;
        const arr = payoutAptsByLessor.get(String(owner)) ?? [];
        arr.push(a);
        payoutAptsByLessor.set(String(owner), arr);
      }

      // Host properties of directly-assigned apartments the owner holds inside
      // someone else's property — fetched only for their title/city.
      const aptPropIds = Array.from(
        new Set(aptRows.map((a) => String(a.property_id)))
      );
      const missingPropIds = aptPropIds.filter((k) => !propMetaById.has(k));
      if (missingPropIds.length > 0) {
        // lessor_id too: it's how an apartment inheriting its owner from the
        // property resolves, which the token-attribution map below needs.
        // collection_mode too: apartments hosted here that don't override it
        // (COL-461 payout readiness) inherit this host property's default.
        const { data, error } = await pagedByColumn<any>(
          "properties",
          "id, title, city, lessor_id, collection_mode, revenue_model, coliviq_fee_pct, coliviq_fee_amount, master_rent",
          "id",
          missingPropIds.map(Number),
          "id",
          true
        );
        warnEnrichment("owners-list apartment host properties", error);
        if (error) payoutDataDegraded = true;
        for (const p of data ?? []) {
          propMetaById.set(String(p.id), {
            title: p.title,
            city: p.city,
            collectionMode: p.collection_mode ?? "company_collects",
            feeConfig: feeConfigFromRow(p),
          });
          if (p.lessor_id) lessorByProp.set(String(p.id), p.lessor_id);
        }
      }

      // COL-461 payout readiness: apartment override ?? host property default
      // ?? "company_collects" — the same COALESCE resolveEffectiveRevenue
      // applies to collection_mode (useProperties.ts:1088-1091), inlined here
      // because only this one field is needed, not a full Apartment/Property.
      const collectionModeByApt = new Map<string, CollectionMode>();
      for (const a of aptRows) {
        collectionModeByApt.set(
          String(a.id),
          a.collection_mode ??
            propMetaById.get(String(a.property_id))?.collectionMode ??
            "company_collects"
        );
      }
      // Effective fee config per apartment: the apartment's own when it sets a
      // revenue model, else the host property's — resolveUnitFeeConfig is the
      // same inheritance generate_management_invoices applies. Payout
      // readiness needs it to tell a group that bills nothing (and so can
      // never be settled by an invoice) from one that does.
      const feeConfigByApt = new Map<string, UnitFeeConfig>();
      for (const a of aptRows) {
        feeConfigByApt.set(
          String(a.id),
          withGeneratorFeeDefault(
            resolveUnitFeeConfig(
              feeConfigFromRow(a),
              propMetaById.get(String(a.property_id))?.feeConfig ?? {
                revenue_model: null,
                coliviq_fee_pct: null,
                coliviq_fee_amount: null,
                master_rent: null,
              }
            )
          )
        );
      }
      const payoutAptIds = aptRows
        .filter(
          (a) => collectionModeByApt.get(String(a.id)) === "company_collects"
        )
        .map((a) => a.id);
      // Properties bill room-level fees directly when a room sits outside any
      // apartment (rooms.apartment_id NULL — pre-apartment-architecture
      // properties, or unassigned rooms). generate_management_invoices groups
      // those under the property itself (property_id, apartment_id=NULL), so
      // payout readiness must too, or a property with only unassigned rooms
      // never gets a group at all. Synthetic apartment id `property-level-<id>`
      // keeps this bucket keyed the same way a real apartment's rooms are.
      const payoutPropertyIds = propIds.filter(
        (id) =>
          (propMetaById.get(String(id))?.collectionMode ??
            "company_collects") === "company_collects"
      );

      // Current-month rent-collected data for those apartments' (+ unassigned
      // property-level) rooms, reusing the exact expected/paid rules
      // usePropertyRentPaidStats.ts uses.
      //
      // These four reads go through that file's `fetchAllByColumn` rather than
      // a bare `.in()`, and for the reason its own doc comment gives: an
      // unpaged `.in()` silently truncates at PostgREST's per-response row cap.
      // Unlike the owner/property/apartment reads above (bounded by ~the owner
      // count), these scale with TENANCY HISTORY — every booking ever made
      // against every company_collects room in the portfolio — so the cap is
      // reachable, and a truncated page would not error: it would quietly drop
      // bookings or payments and hand `computeOwnerPayoutGroups` a smaller
      // expected or paid figure, i.e. the wrong answer to "is this owner's
      // rent fully collected".
      //
      // Each call still degrades to [] rather than throwing, matching the
      // rest of this query (see warnEnrichment): a missing column on a
      // drifted dev database must cost the payout tile, not the whole owners
      // list. But unlike the other enrichments here, [] and "genuinely no
      // rows" are indistinguishable to computeOwnerPayoutGroups, and this
      // result GATES a "go pay this owner" signal rather than merely
      // decorating the UI — a dropped page of bookings/payments can read as
      // "fully collected" instead of "unknown". payoutDataDegraded tracks
      // that any read feeding the tile failed — the four below AND the
      // apartment/property enrichments above — so every owner's
      // readyPayoutGroups is suppressed to 0 for this render rather than
      // computed against a silently-partial dataset: a real fetch failure
      // must never surface a false "ready".
      const payoutFetch = async <Row>(
        scope: string,
        run: () => Promise<Row[]>
      ): Promise<Row[]> => {
        try {
          return await run();
        } catch (error) {
          warnEnrichment(scope, error);
          payoutDataDegraded = true;
          return [];
        }
      };

      // Earlier months that still hold an unpaid offset invoice (COL-515), per
      // owner. Only these months are ever computed, and their rent is read
      // separately below — an owner with nothing open costs the reads nothing,
      // and this month's reads are exactly what they were.
      const allInvByLessor = new Map<string, any[]>();
      for (const inv of invRes.data ?? []) {
        const arr = allInvByLessor.get(inv.lessor_id) ?? [];
        arr.push(inv);
        allInvByLessor.set(inv.lessor_id, arr);
      }
      const openPastByLessor = new Map<string, Map<string, Set<string>>>();
      let earliestOpenMonth: string | null = null;
      for (const [lessorId, lessorInvoices] of allInvByLessor) {
        const open = openOffsetPropertiesByMonth(
          lessorInvoices,
          lessorId,
          ymKey
        );
        if (open.size === 0) continue;
        openPastByLessor.set(lessorId, open);
        for (const month of open.keys()) {
          if (earliestOpenMonth === null || month < earliestOpenMonth) {
            earliestOpenMonth = month;
          }
        }
      }

      let payoutRooms: PayoutRoomRow[] = [];
      let payoutBookings: PayoutBookingRow[] = [];
      let payoutPayments: PayoutPaymentRow[] = [];
      let earlierPayments: PayoutPaymentRow[] = [];
      let payoutProofs: { payment_id: string; status: string | null }[] = [];
      if (payoutAptIds.length > 0 || payoutPropertyIds.length > 0) {
        const [aptRooms, propRooms] = await Promise.all([
          payoutFetch<any>("owners-list payout rooms (apartment)", () =>
            fetchAllByColumn<any>(
              "rooms",
              "id, title, apartment_id, price, status, available",
              "apartment_id",
              payoutAptIds,
              "id",
              true
            )
          ),
          payoutFetch<any>("owners-list payout rooms (property-level)", () =>
            fetchAllByColumn<any>(
              "rooms",
              "id, title, property_id, price, status, available",
              "property_id",
              payoutPropertyIds,
              "id",
              true,
              (q: any) => q.is("apartment_id", null)
            )
          ),
        ]);
        payoutRooms = [
          ...aptRooms.map((r) => ({
            id: r.id,
            apartmentId: r.apartment_id,
            title: r.title,
            price: r.price,
            status: r.status,
            available: r.available,
          })),
          ...propRooms.map((r) => ({
            id: r.id,
            apartmentId: `property-level-${r.property_id}`,
            title: r.title,
            price: r.price,
            status: r.status,
            available: r.available,
          })),
        ];

        const roomIds = payoutRooms.map((r) => r.id);
        if (roomIds.length > 0) {
          const bookingRows = await payoutFetch<any>(
            "owners-list payout bookings",
            () =>
              fetchAllByColumn<any>(
                "bookings",
                "id, room_id, name, monthly_rent, first_month_rent_override, status, start_date, end_date, admin_archived_at, actual_move_in_date, move_in_date, actual_move_out_date, move_out_date, rent_discount_mode, rent_discount_value, rent_discount_months, rent_discount_start_month",
                "room_id",
                roomIds,
                "id",
                true
              )
          );
          payoutBookings = bookingRows.map((b) => ({
            ...b,
            roomId: b.room_id,
          }));

          const bookingIds = payoutBookings.map((b) => b.id);
          if (bookingIds.length > 0) {
            const month = currentMonthKey(now);
            const paymentRows = await payoutFetch<any>(
              "owners-list payout payments",
              () =>
                fetchAllByColumn<any>(
                  "payments",
                  "id, booking_id, amount_cents, gross_amount_cents, received_cents, status, due_date, stripe_payment_intent_id, stripe_charge_id, paid_at, marked_paid_at, marked_paid_by",
                  "booking_id",
                  bookingIds,
                  "id",
                  true,
                  (q: any) =>
                    q
                      .eq("type", "rent")
                      .gte("due_date", `${month}-01`)
                      .lt("due_date", nextMonthStart(month))
                )
            );
            payoutPayments = paymentRows.map((p) => ({
              ...p,
              bookingId: p.booking_id,
            }));

            const paymentIds = payoutPayments.map((p) => p.id);
            if (paymentIds.length > 0) {
              // created_at desc is load-bearing: latestProofByPaymentId takes
              // the FIRST row per payment as the latest one.
              payoutProofs = await payoutFetch<{
                payment_id: string;
                status: string | null;
              }>("owners-list payout proofs", () =>
                fetchAllByColumn<{
                  payment_id: string;
                  status: string | null;
                }>(
                  "payment_proofs",
                  "payment_id, status, created_at",
                  "payment_id",
                  paymentIds,
                  "created_at",
                  false
                )
              );
            }

            // Rent of the earlier months that still hold an unpaid offset
            // invoice (COL-515). A read of its own, and only for the bookings
            // of the properties those invoices are for: widening the read
            // above would page through every tenancy's rent since the oldest
            // open month for the whole portfolio on every load. A failure
            // here degrades like the other payout reads (payoutDataDegraded
            // blanks the tile and the view for every owner): the tile counts
            // the carried-over payouts too, so a band that did not load would
            // read as "nothing is owed from earlier", which is unknown.
            if (earliestOpenMonth !== null) {
              const since: string = earliestOpenMonth;
              const openProperties = new Set<string>();
              for (const open of openPastByLessor.values()) {
                for (const properties of open.values()) {
                  for (const id of properties) openProperties.add(id);
                }
              }
              const propertyByApt = new Map(
                aptRows.map((a) => [String(a.id), String(a.property_id)])
              );
              const earlierRoomIds = new Set(
                payoutRooms
                  .filter((r) => {
                    const apt = String(r.apartmentId);
                    const property = apt.startsWith("property-level-")
                      ? apt.slice("property-level-".length)
                      : propertyByApt.get(apt);
                    return (
                      property !== undefined && openProperties.has(property)
                    );
                  })
                  .map((r) => String(r.id))
              );
              const earlierBookingIds = payoutBookings
                .filter(
                  (b) =>
                    b.roomId != null && earlierRoomIds.has(String(b.roomId))
                )
                .map((b) => b.id);
              if (earlierBookingIds.length > 0) {
                const earlierRows = await payoutFetch<any>(
                  "owners-list payout payments (earlier months)",
                  () =>
                    fetchAllByColumn<any>(
                      "payments",
                      "id, booking_id, amount_cents, gross_amount_cents, received_cents, status, due_date, stripe_payment_intent_id, stripe_charge_id, paid_at, marked_paid_at, marked_paid_by",
                      "booking_id",
                      earlierBookingIds,
                      "id",
                      true,
                      (q: any) =>
                        q
                          .eq("type", "rent")
                          .gte("due_date", `${since}-01`)
                          .lt("due_date", `${month}-01`)
                    )
                );
                earlierPayments = earlierRows.map((p) => ({
                  ...p,
                  bookingId: p.booking_id,
                }));
                const earlierPaymentIds = earlierPayments.map((p) => p.id);
                if (earlierPaymentIds.length > 0) {
                  const earlierProofs = await payoutFetch<{
                    payment_id: string;
                    status: string | null;
                  }>("owners-list payout proofs (earlier months)", () =>
                    fetchAllByColumn<{
                      payment_id: string;
                      status: string | null;
                    }>(
                      "payment_proofs",
                      "payment_id, status, created_at",
                      "payment_id",
                      earlierPaymentIds,
                      "created_at",
                      false
                    )
                  );
                  payoutProofs = [...payoutProofs, ...earlierProofs];
                }
              }
            }
          }
        }
      }

      /**
       * Effective owner per apartment id. An apartment-scoped token is only the
       * property holder's if the apartment is theirs — otherwise a lessor who
       * onboards their own apartment inside someone else's building would show
       * up as that building owner's onboarding.
       */
      const ownerByApartment = new Map<string, string | null>();
      const resolveAptOwner = (a: ListAptRow) =>
        effectiveApartmentOwner(
          a.lessor_id,
          lessorByProp.get(String(a.property_id)),
          a.owner_user_id ? lessorByOwnerUser[a.owner_user_id] : null
        );
      for (const a of aptRows)
        ownerByApartment.set(String(a.id), resolveAptOwner(a));

      // Tokens (with their submissions) per property, so each owner's stage can
      // be resolved against the tokens ACTUALLY ATTRIBUTABLE TO THEM. Tokens are
      // property-scoped but ownership is apartment-level, so a property hosting
      // several lessors' apartments must not credit them all with whichever one
      // onboarded — see ownerPropertyOnboardingStage.
      const tokensByProp = new Map<string, OnboardingTokenWithSubmissions[]>();
      const stagePropIds = Array.from(
        new Set([...propIds.map(String), ...aptPropIds])
      ).map(Number);
      if (stagePropIds.length > 0) {
        const { rows: tokenRows, error: tokenErr } =
          await fetchOnboardingTokens(stagePropIds);
        // Degrade like useOwnerDetail does: a failed onboarding-stage lookup
        // should leave every owner reading "pending", not kill the whole list.
        warnEnrichment("owners-list onboarding tokens", tokenErr);
        const tokenById = new Map<string, OnboardingTokenWithSubmissions>();
        for (const tk of tokenRows ?? []) {
          const token: OnboardingTokenWithSubmissions = {
            id: String(tk.id),
            email: tk.email ?? null,
            apartment_id: tk.apartment_id ?? null,
            submissions: [],
          };
          tokenById.set(token.id, token);
          const k = String(tk.property_id);
          const list = tokensByProp.get(k) ?? [];
          list.push(token);
          tokensByProp.set(k, list);
        }
        // Apartments named by a token that no owner on this page holds (a
        // search-filtered list sees only the matching owners' apartments) —
        // resolve their owner too, so their tokens can't be credited to the
        // property holder.
        const unknownTokenAptIds = Array.from(
          new Set(
            (tokenRows ?? [])
              .map((t: any) => t.apartment_id)
              .filter((v: unknown) => v != null)
              .map(String)
          )
        ).filter((k) => !ownerByApartment.has(k));
        if (unknownTokenAptIds.length > 0) {
          const { data, error } = await supabase
            .from("apartments")
            .select("id, label, property_id, lessor_id, owner_user_id")
            .in("id", unknownTokenAptIds.map(Number));
          warnEnrichment("owners-list token apartments", error);
          for (const a of (data ?? []) as ListAptRow[])
            ownerByApartment.set(String(a.id), resolveAptOwner(a));
        }
        const tokenIds = Array.from(tokenById.keys());
        if (tokenIds.length > 0) {
          // Every submission, not just verified ones: the email on a submission
          // is one of the ways a token is attributed to an owner.
          const { data: subs, error: subErr } = await supabase
            .from("owner_onboarding_submissions")
            .select("token_id, status, email")
            .in("token_id", tokenIds);
          warnEnrichment("owners-list onboarding submissions", subErr);
          for (const s of subs ?? []) {
            const token = tokenById.get(String(s.token_id));
            if (!token) continue;
            (
              token.submissions as { status: string; email: string | null }[]
            ).push({ status: s.status, email: s.email ?? null });
          }
        }
      }

      // All current-month invoices per lessor (for the worst-status badge).
      const currentInvByLessor = new Map<string, any[]>();
      for (const inv of invRes.data ?? []) {
        if (String(inv.period_month).slice(0, 7) === ymKey) {
          const arr = currentInvByLessor.get(inv.lessor_id) ?? [];
          arr.push(inv);
          currentInvByLessor.set(inv.lessor_id, arr);
        }
      }

      // Earlier months' rent by the month it fell due (COL-515): each month's
      // groups must see only their own month's rent.
      const earlierPaymentsByMonth = groupPaymentsByDueMonth(earlierPayments);

      return rows.map((l) => {
        const props = propsByLessor.get(l.id) ?? [];
        const ownedPropKeys = new Set(props.map((p) => String(p.id)));
        /**
         * This owner's stage — never the neighbours'. Pass an apartment id to
         * read that apartment's own invitation (property-wide invites still
         * count); omit it for the property-level rollup.
         */
        const stageForProp = (
          propKey: string,
          apartmentId?: number | string | null
        ): OnboardingStage =>
          ownerPropertyOnboardingStage(
            l.email,
            ownedPropKeys.has(propKey),
            tokensByProp.get(propKey) ?? [],
            apartmentId,
            // A neighbour's apartment-scoped token is their onboarding, not
            // this owner's, even inside a property this owner holds.
            (aptKey) => {
              const owner = ownerByApartment.get(aptKey);
              return owner != null && String(owner) !== String(l.id);
            }
          );
        // Every property the owner has a stake in: held directly, plus the
        // ones hosting their apartments. Onboarding tokens hang off properties,
        // so an apartment-only owner would otherwise have nothing to aggregate
        // and read "—" forever, even with a verified submission on file.
        const stakeProps = new Map<
          string,
          { id: number; title: string | null; city: string | null }
        >();
        for (const p of props) stakeProps.set(String(p.id), p);
        for (const a of aptsByLessor.get(String(l.id)) ?? []) {
          const k = String(a.property_id);
          if (stakeProps.has(k)) continue;
          const meta = propMetaById.get(k);
          stakeProps.set(k, {
            id: Number(a.property_id),
            title: meta?.title ?? null,
            city: meta?.city ?? null,
          });
        }
        const propertyList: OwnerListProperty[] = Array.from(
          stakeProps.values()
        )
          .map((p) => ({
            ...p,
            onboardingStage: stageForProp(String(p.id)),
            heldDirectly: ownedPropKeys.has(String(p.id)),
          }))
          .sort((x, y) => (x.title ?? "").localeCompare(y.title ?? ""));
        // Owner stage = lowest (least-progressed) across their properties.
        const onboardingStage = lowestOnboardingStage(
          propertyList.map((p) => p.onboardingStage)
        );
        // Apartments the owner holds (effective lessor), ordered by property
        // then label so the expanded row reads property-by-property.
        const apartmentList: OwnerListApartment[] = (
          aptsByLessor.get(String(l.id)) ?? []
        )
          .map((a) => {
            const meta = propMetaById.get(String(a.property_id));
            return {
              id: Number(a.id),
              label: a.label ?? "—",
              propertyId: Number(a.property_id),
              propertyTitle: meta?.title ?? null,
              propertyCity: meta?.city ?? null,
              onboardingStage: stageForProp(String(a.property_id), a.id),
              apartmentScoped:
                a.lessor_id != null ||
                !ownedPropKeys.has(String(a.property_id)),
            };
          })
          .sort(
            (x, y) =>
              (x.propertyTitle ?? "").localeCompare(y.propertyTitle ?? "") ||
              x.label.localeCompare(y.label, undefined, { numeric: true })
          );
        const payoutApartmentInputs: PayoutApartmentInput[] = (
          payoutAptsByLessor.get(String(l.id)) ?? []
        ).map((a) => ({
          id: a.id,
          propertyId: a.property_id,
          label: a.label,
          collectionMode:
            collectionModeByApt.get(String(a.id)) ?? "company_collects",
          feeConfig:
            feeConfigByApt.get(String(a.id)) ??
            withGeneratorFeeDefault({
              revenue_model: null,
              coliviq_fee_pct: null,
              coliviq_fee_amount: null,
              master_rent: null,
            }),
        }));
        // Unassigned (apartment_id NULL) rooms bill under the property
        // itself, not any apartment — only this owner's DIRECTLY held
        // properties (`props`, same as propsByLessor above) can attribute
        // that bucket to them. Synthetic id matches the `property-level-<id>`
        // key payoutRooms was tagged with above.
        for (const p of props) {
          payoutApartmentInputs.push({
            id: `property-level-${p.id}`,
            propertyId: p.id,
            collectionMode:
              propMetaById.get(String(p.id))?.collectionMode ??
              "company_collects",
            // Rooms outside any apartment bill on the PROPERTY's own config —
            // there is no apartment level to inherit from.
            feeConfig: withGeneratorFeeDefault(
              propMetaById.get(String(p.id))?.feeConfig ?? {
                revenue_model: null,
                coliviq_fee_pct: null,
                coliviq_fee_amount: null,
                master_rent: null,
              }
            ),
          });
        }
        // A degraded read must never report a false "ready" — see
        // payoutDataDegraded above.
        const payoutGroups = payoutDataDegraded
          ? []
          : computeOwnerPayoutGroups({
              lessorId: l.id,
              apartments: payoutApartmentInputs,
              rooms: payoutRooms,
              bookings: payoutBookings,
              payments: payoutPayments,
              proofs: payoutProofs,
              invoices: currentInvByLessor.get(l.id) ?? [],
              now,
            });
        const readyPayoutGroups = countReadyPayoutGroups(payoutGroups);
        // Payouts still open from earlier months (COL-515). Like payoutGroups,
        // never from a degraded read.
        const openPast = openPastByLessor.get(l.id);
        const carriedOverPayoutGroups =
          payoutDataDegraded || !openPast
            ? []
            : computeCarriedOverPayoutGroups({
                lessorId: l.id,
                apartments: payoutApartmentInputs,
                rooms: payoutRooms,
                bookings: payoutBookings,
                proofs: payoutProofs,
                invoices: allInvByLessor.get(l.id) ?? [],
                paymentsByMonth: earlierPaymentsByMonth,
                openMonths: openPast,
              });
        return {
          ...l,
          apartmentsCount: apartmentList.length,
          apartmentList,
          propertiesCount: props.length,
          currentInvoiceStatus: worstInvoiceStatus(
            currentInvByLessor.get(l.id) ?? []
          ),
          readyPayoutGroups,
          payoutGroups,
          carriedOverPayoutGroups,
          payoutDataDegraded,
          onboardingStage,
          propertyList,
        };
      });
    },
  });
}

export function useOwnerDetail(lessor: Lessor | null) {
  return useQuery({
    queryKey: OWNER_DETAIL_KEY(lessor?.id ?? null),
    enabled: !!lessor,
    queryFn: async (): Promise<OwnerDetail> => {
      if (!lessor) throw new Error("no owner");

      // 1. The owner's portfolio is APARTMENT-level. An apartment belongs to
      // this owner when its EFFECTIVE lessor is them —
      // COALESCE(apartments.lessor_id, properties.lessor_id) — the same rule
      // generate_management_invoices bills by. That means two sources:
      //   a) apartments inside the properties they hold (lessor_id NULL there,
      //      so ownership is inherited from the property), and
      //   b) apartments assigned directly to them, which may sit inside a
      //      property held by a DIFFERENT lessor.
      const PROP_COLS =
        "id, lessor_id, title, city, zip_code, address, image_url, images, revenue_model, coliviq_fee_pct, coliviq_fee_amount, collection_mode, master_rent";
      const APT_COLS =
        "id, label, floor, property_id, lessor_id, owner_user_id, revenue_model, coliviq_fee_pct, coliviq_fee_amount, collection_mode, master_rent";

      const [ownedPropRes, directAptRes, lessorByOwnerUser] = await Promise.all(
        [
          supabase
            .from("properties")
            .select(PROP_COLS)
            .eq("lessor_id", lessor.id),
          supabase
            .from("apartments")
            .select(APT_COLS)
            .eq("lessor_id", lessor.id),
          fetchLessorIdByOwnerUser(),
        ]
      );
      if (ownedPropRes.error) throw ownedPropRes.error;
      if (directAptRes.error) throw directAptRes.error;

      // Owner ACCOUNTS that resolve to this lessor — apartments naming one of
      // them in owner_user_id are theirs too (see owner-user-lessor).
      const accountUserIds = Object.entries(lessorByOwnerUser)
        .filter(([, lessorId]) => String(lessorId) === String(lessor.id))
        .map(([userId]) => userId);

      const ownedPropRows = (ownedPropRes.data ?? []) as any[];
      const ownedPropIds = ownedPropRows.map((p) => Number(p.id));
      const ownedPropIdSet = new Set(ownedPropIds.map(String));

      // Apartments inheriting ownership from one of the owner's properties.
      let inheritedAptRows: any[] = [];
      if (ownedPropIds.length > 0) {
        const { data, error } = await supabase
          .from("apartments")
          .select(APT_COLS)
          .in("property_id", ownedPropIds)
          .is("lessor_id", null);
        if (error) throw error;
        inheritedAptRows = data ?? [];
      }
      // Apartments owned through this owner's ACCOUNT rather than a lessor —
      // they can sit in any property, including one nobody on the list holds.
      let accountAptRows: any[] = [];
      if (accountUserIds.length > 0) {
        const { data, error } = await supabase
          .from("apartments")
          .select(APT_COLS)
          .in("owner_user_id", accountUserIds)
          .is("lessor_id", null);
        warnEnrichment("apartments (owner account)", error);
        accountAptRows = data ?? [];
      }
      // Direct rows carry a lessor_id; the other two don't. Dedupe by id — an
      // inherited apartment can also name this owner's account.
      const aptRowById = new Map<string, any>();
      for (const a of [
        ...((directAptRes.data ?? []) as any[]),
        ...inheritedAptRows,
        ...accountAptRows,
      ]) {
        aptRowById.set(String(a.id), a);
      }
      const aptRows = Array.from(aptRowById.values());

      // Host properties of directly-assigned apartments that sit outside the
      // owner's own properties — needed for the address shown on the card and
      // for the fee config the apartment inherits.
      const hostPropIds = Array.from(
        new Set(aptRows.map((a) => String(a.property_id)))
      ).filter((k) => !ownedPropIdSet.has(k));
      let hostPropRows: any[] = [];
      if (hostPropIds.length > 0) {
        const { data, error } = await supabase
          .from("properties")
          .select(PROP_COLS)
          .in("id", hostPropIds.map(Number));
        warnEnrichment("apartment host properties", error);
        hostPropRows = data ?? [];
      }

      // Keys are stringified throughout: property/apartment/room ids are bigint
      // and PostgREST doesn't narrow them to JS numbers consistently across
      // queries, so a Map<number, …> can silently miss every match.
      const propMetaById = new Map<string, OwnerPropertyMeta>();
      /** Lessor holding each relevant property — for apartment-owner resolution. */
      const lessorByProp = new Map<string, string>();
      for (const p of [...ownedPropRows, ...hostPropRows]) {
        if (p.lessor_id) lessorByProp.set(String(p.id), p.lessor_id);
        propMetaById.set(String(p.id), {
          id: Number(p.id),
          title: p.title,
          city: p.city,
          zip_code: p.zip_code ?? null,
          address: p.address,
          cover_image: resolveCoverImage(p.images, p.image_url),
          collection_mode: (p.collection_mode ?? null) as CollectionMode | null,
          config: {
            revenue_model: (p.revenue_model ?? null) as RevenueModel | null,
            coliviq_fee_pct:
              p.coliviq_fee_pct != null ? Number(p.coliviq_fee_pct) : null,
            coliviq_fee_amount:
              p.coliviq_fee_amount != null
                ? Number(p.coliviq_fee_amount)
                : null,
            master_rent: p.master_rent != null ? Number(p.master_rent) : null,
          },
        });
      }
      /** Every property the owner has something in — owned or hosting a unit. */
      const relevantPropIds = Array.from(propMetaById.keys()).map(Number);

      /** Build the card for one unit (an apartment, or a property remainder). */
      function makeUnit(
        prop: OwnerPropertyMeta,
        apt: {
          id: number;
          label: string | null;
          floor: string | null;
          config: UnitFeeConfig;
          collection_mode: CollectionMode | null;
        } | null
      ): OwnerApartment {
        const config = resolveUnitFeeConfig(apt?.config, prop.config);
        const collection_mode = apt?.collection_mode ?? prop.collection_mode;
        return {
          id: apt?.id ?? null,
          label: apt ? (apt.label ?? "—") : "Rooms without an apartment",
          floor: apt?.floor ?? null,
          unassigned: !apt,
          property_id: prop.id,
          property_title: prop.title,
          property_city: prop.city,
          property_zip: prop.zip_code,
          property_address: prop.address,
          cover_image: prop.cover_image,
          rooms_count: 0,
          onboarding_stage: "pending",
          onboarding_label: ONBOARDING_STAGE_LABEL.pending,
          current_tenants: [],
          revenue_model: config.revenue_model,
          revenue_model_label: propertyRevenueModelLabel(config.revenue_model),
          collection_mode,
          collection_label: propertyCollectionLabel(collection_mode),
          coliviq_fee_pct: config.coliviq_fee_pct,
          coliviq_fee_amount: config.coliviq_fee_amount,
          master_rent: config.master_rent,
          occupied_rent: 0,
          monthly_revenue: 0,
          generator_occupied_rooms: 0,
          generator_occupied_rent: 0,
          rooms_degraded: false,
          revenue_degraded: false,
        };
      }

      // The owner's apartments, ordered by property then label.
      const apartments: OwnerApartment[] = aptRows
        .map((a) => {
          const prop = propMetaById.get(String(a.property_id));
          if (!prop) return null;
          return makeUnit(prop, {
            id: Number(a.id),
            label: a.label,
            floor: a.floor ?? null,
            collection_mode: (a.collection_mode ??
              null) as CollectionMode | null,
            config: {
              revenue_model: (a.revenue_model ?? null) as RevenueModel | null,
              coliviq_fee_pct:
                a.coliviq_fee_pct != null ? Number(a.coliviq_fee_pct) : null,
              coliviq_fee_amount:
                a.coliviq_fee_amount != null
                  ? Number(a.coliviq_fee_amount)
                  : null,
              master_rent: a.master_rent != null ? Number(a.master_rent) : null,
            },
          });
        })
        .filter((u): u is OwnerApartment => u !== null)
        .sort(
          (x, y) =>
            (x.property_title ?? "").localeCompare(y.property_title ?? "") ||
            x.label.localeCompare(y.label, undefined, { numeric: true })
        );
      /** Apartments this owner holds — a room in any other one isn't theirs. */
      const ownedAptIdSet = new Set(apartments.map((a) => String(a.id)));

      // Per-property onboarding stage, resolved against the tokens ATTRIBUTABLE
      // TO THIS OWNER: no token of theirs → pending; a token (invite sent /
      // signed) → invite sent; a VERIFIED submission → onboarded. A property
      // that merely HOSTS one of their apartments contributes only if the invite
      // was addressed to them — otherwise the building's other lessor's
      // onboarding would show up as this owner's. Returns the map rather than
      // writing to the units, because it runs concurrently with the room
      // enrichment below (#8), which is what creates the property-level units.
      // Resolves a stage per (property, apartment) rather than per property:
      // invitations are apartment-scoped now, so sibling apartments in one
      // building can legitimately sit at different stages.
      type StageResolver = (
        propertyId: number | string,
        apartmentId?: number | string | null
      ) => OnboardingStage;
      type OnboardingResolution = {
        stage: StageResolver;
        /**
         * Tokens scoped to an apartment held by a DIFFERENT lessor. The
         * submission fallback below pulls tokens by property, so without this
         * it would surface a neighbour's submission — their IBAN, their signed
         * contracts — under this owner, contradicting the stage that
         * (correctly) reads pending. Empty on a pre-apartment-scope database,
         * where no token names an apartment at all.
         */
        foreignTokenIds: Set<string>;
      };
      const onboardingEnrichment = (async (): Promise<OnboardingResolution> => {
        if (relevantPropIds.length === 0)
          return { stage: () => "pending", foreignTokenIds: new Set() };
        const { rows: tokenRows, error: tokenErr } =
          await fetchOnboardingTokens(relevantPropIds);
        warnEnrichment("onboarding tokens", tokenErr);
        const tokenById = new Map<string, OnboardingTokenWithSubmissions>();
        const tokensByProp = new Map<
          string,
          OnboardingTokenWithSubmissions[]
        >();
        for (const tk of tokenRows ?? []) {
          const token: OnboardingTokenWithSubmissions = {
            id: String(tk.id),
            email: tk.email ?? null,
            apartment_id: tk.apartment_id ?? null,
            submissions: [],
          };
          tokenById.set(token.id, token);
          const k = String(tk.property_id);
          const list = tokensByProp.get(k) ?? [];
          list.push(token);
          tokensByProp.set(k, list);
        }
        // Who holds the apartments those tokens name? An apartment-scoped token
        // for a NEIGHBOUR's apartment is that lessor's onboarding, even when it
        // sits in a property this owner holds — crediting it here would make
        // the owner read "Onboarded" without ever having onboarded.
        const ownerByApartment = new Map<string, string | null>();
        const tokenAptIds = Array.from(
          new Set(
            (tokenRows ?? [])
              .map((t: any) => t.apartment_id)
              .filter((v: unknown) => v != null)
              .map(String)
          )
        );
        if (tokenAptIds.length > 0) {
          const { data, error } = await supabase
            .from("apartments")
            .select("id, property_id, lessor_id, owner_user_id")
            .in("id", tokenAptIds.map(Number));
          warnEnrichment("onboarding token apartments", error);
          for (const a of data ?? []) {
            ownerByApartment.set(
              String(a.id),
              effectiveApartmentOwner(
                a.lessor_id,
                lessorByProp.get(String(a.property_id)),
                a.owner_user_id ? lessorByOwnerUser[a.owner_user_id] : null
              )
            );
          }
        }
        const tokenIds = Array.from(tokenById.keys());
        if (tokenIds.length > 0) {
          // Every submission, not just verified ones — a submission's email is
          // one of the ways a token is attributed to this owner.
          const { data: subs, error: subErr } = await supabase
            .from("owner_onboarding_submissions")
            .select("token_id, status, email")
            .in("token_id", tokenIds);
          warnEnrichment("onboarding submissions", subErr);
          for (const s of subs ?? []) {
            const token = tokenById.get(String(s.token_id));
            if (!token) continue;
            (
              token.submissions as { status: string; email: string | null }[]
            ).push({ status: s.status, email: s.email ?? null });
          }
        }
        const ownedKeys = new Set(ownedPropIds.map(String));
        const isOtherOwnersApartment = (aptKey: string) => {
          const owner = ownerByApartment.get(aptKey);
          return owner != null && String(owner) !== String(lessor.id);
        };
        const foreignTokenIds = new Set(
          Array.from(tokenById.values())
            .filter(
              (t) =>
                t.apartment_id != null &&
                isOtherOwnersApartment(String(t.apartment_id))
            )
            .map((t) => t.id)
        );
        return {
          stage: (propertyId, apartmentId) =>
            ownerPropertyOnboardingStage(
              lessor.email,
              ownedKeys.has(String(propertyId)),
              tokensByProp.get(String(propertyId)) ?? [],
              apartmentId,
              isOtherOwnersApartment
            ),
          foreignTokenIds,
        };
      })();

      // Rooms, occupancy and tenants, aggregated per UNIT. A room belongs to
      // this owner when it sits in an apartment they hold, or — for rooms not
      // assigned to any apartment — in a property they hold. Rooms of another
      // lessor's apartment inside the owner's property are NOT theirs, and rooms
      // of the owner's apartment inside another lessor's property ARE. Returns
      // the property-level units it discovers (rooms with no apartment), which
      // only exist once the rooms are known.
      const roomsEnrichment = (async (): Promise<OwnerApartment[]> => {
        if (relevantPropIds.length === 0) return [];
        const { data: roomRows, error: roomErr } = await supabase
          .from("rooms")
          .select(
            "id, title, property_id, apartment_id, price, status, available"
          )
          .in("property_id", relevantPropIds);
        warnEnrichment("rooms", roomErr);
        const roomsFailed = !!roomErr;

        /** Unit key: an apartment, else the property's unassigned remainder. */
        const unitKeyFor = (aptId: unknown, propId: unknown) =>
          aptId != null ? `apt:${String(aptId)}` : `prop:${String(propId)}`;

        // Property-level units, created lazily for properties that actually
        // have rooms outside any apartment.
        const unassignedByProp = new Map<string, OwnerApartment>();
        const unitByKey = new Map<string, OwnerApartment>(
          apartments.map((a) => [`apt:${String(a.id)}`, a])
        );
        const roomIds: number[] = [];
        const roomToUnit = new Map<string, string>();
        const roomTitle = new Map<string, string | null>();
        const roomPrice = new Map<string, number>();
        for (const r of roomRows ?? []) {
          if (!roomBelongsToOwner(r, ownedAptIdSet, ownedPropIdSet)) continue;
          const key = unitKeyFor(r.apartment_id, r.property_id);
          let unit = unitByKey.get(key);
          if (!unit) {
            // Only reachable for a property-level remainder: apartment units
            // are all pre-seeded above.
            const prop = propMetaById.get(String(r.property_id));
            if (!prop) continue;
            unit = makeUnit(prop, null);
            unitByKey.set(key, unit);
            unassignedByProp.set(String(r.property_id), unit);
          }
          unit.rooms_count += 1;
          // The generator's own occupancy test, verbatim — this is what decides
          // whether the property produces a fee line at all.
          if (r.status === "occupied" || r.available === false) {
            unit.generator_occupied_rooms += 1;
            unit.generator_occupied_rent += Number(r.price ?? 0);
          }
          const rid = String(r.id);
          roomIds.push(Number(r.id));
          roomToUnit.set(rid, key);
          roomTitle.set(rid, r.title ?? null);
          roomPrice.set(rid, Number(r.price ?? 0));
        }

        // Occupancy — and therefore the rent that feeds CoLiviq's revenue basis
        // — is driven by BOOKINGS, not the rooms.status flag (which isn't
        // reliably maintained). A room counts as occupied, and its price counts
        // toward occupied rent, when it has a CURRENT booking (isActiveBooking
        // AND roomOccupancyPhase === "current" — an active booking whose move-in
        // has actually happened, not a future-dated "upcoming" one). This keeps
        // "Total monthly revenue" consistent with the tenants shown below it.
        //
        // Revenue is billed PER UNIT with the unit's EFFECTIVE (apartment→
        // property) fee config, mirroring the DB generate_management_invoices
        // generator — makeUnit already resolved that config.
        const occupiedRoomsByUnit = new Map<string, number>();
        // Set when the bookings query fails outright (both attempts below) —
        // occupied_rent/monthly_revenue/current_tenants stay at their zero
        // defaults in that case, but that's an UNKNOWN reading, not a verified
        // vacancy, so callers must render "—" instead of €0.00.
        let bookingsFailed = false;
        if (roomIds.length > 0) {
          // `rent_paid_through` sharpens the rent-status badge but isn't present
          // in every environment's schema. Try the full select; if that column
          // is missing, fall back to one without it — isPaidCurrentPeriod then
          // degrades cleanly to receipt-time detection rather than the whole
          // query (and thus the tenant list) failing.
          const BOOKING_COLS =
            "id, user_id, room_id, name, email, status, admin_archived_at, manually_marked_paid, payment_status, last_payment_received_at, rent_due_date, start_date, move_in_date, actual_move_in_date, move_out_date, actual_move_out_date, key_returned_at";
          let { data: bookingRows, error: bookingErr } = await supabase
            .from("bookings")
            .select(`${BOOKING_COLS}, rent_paid_through`)
            .in("room_id", roomIds)
            .order("id", { ascending: false });
          if (bookingErr) {
            const fallback = await supabase
              .from("bookings")
              .select(BOOKING_COLS)
              .in("room_id", roomIds)
              .order("id", { ascending: false });
            // Rows without rent_paid_through; every reader treats it as optional.
            bookingRows = fallback.data as unknown as typeof bookingRows;
            bookingErr = fallback.error;
          }
          warnEnrichment("current tenants (bookings)", bookingErr);
          bookingsFailed = !!bookingErr;
          // The CURRENT booking per room (first seen wins — ordered id desc,
          // so a re-let's superseding booking is picked over an older one at
          // the same phase). roomOccupancyPhase === "current" is stricter
          // than the old isActiveBooking check: isActiveBooking alone also
          // matches "upcoming" (a signed booking whose move-in is still in
          // the future), which inflated occupied_rent / monthly revenue and,
          // worse, let a future move-in with a higher booking id displace the
          // actual sitting tenant in this same dedup. "Current tenants" must
          // mean tenants who have actually moved in, not who's about to.
          const bookingByRoom = new Map<number, any>();
          for (const b of bookingRows ?? []) {
            // isActiveBooking first: a tenant in their notice period counts as
            // current on the room page, but occupied rent here is unchanged.
            if (!isActiveBooking(b) || roomOccupancyPhase(b) !== "current")
              continue;
            if (!bookingByRoom.has(b.room_id)) bookingByRoom.set(b.room_id, b);
          }
          // Charge each occupied room's rent to its billable unit.
          for (const roomId of bookingByRoom.keys()) {
            const key = roomToUnit.get(String(roomId));
            const unit = key ? unitByKey.get(key) : undefined;
            if (!key || !unit) continue;
            unit.occupied_rent += roomPrice.get(String(roomId)) ?? 0;
            occupiedRoomsByUnit.set(
              key,
              (occupiedRoomsByUnit.get(key) ?? 0) + 1
            );
          }
          const chosen = Array.from(bookingByRoom.values());
          const userIds = Array.from(
            new Set(chosen.map((b) => b.user_id).filter(Boolean))
          );
          const profileById = new Map<
            string,
            { name: string | null; email: string | null }
          >();
          if (userIds.length > 0) {
            const { data: profRows } = await supabase
              .from("profiles")
              .select("id, name, email")
              .in("id", userIds);
            for (const pr of profRows ?? [])
              profileById.set(pr.id, { name: pr.name, email: pr.email });
          }
          const rentStatusFor = (b: any): OwnerPropertyTenant["rentStatus"] => {
            if (isPaidCurrentPeriod(b)) return "paid";
            const s = String(b.payment_status ?? "");
            return s.startsWith("overdue") || s === "inkasso"
              ? "overdue"
              : "pending";
          };
          for (const b of chosen) {
            const key = roomToUnit.get(String(b.room_id));
            const unit = key ? unitByKey.get(key) : undefined;
            if (!unit) continue;
            const prof = b.user_id ? profileById.get(b.user_id) : undefined;
            unit.current_tenants.push({
              bookingId: b.id,
              roomId: b.room_id,
              roomTitle: roomTitle.get(String(b.room_id)) ?? null,
              name: prof?.name || b.name || "Tenant",
              email: prof?.email || b.email || null,
              rentStatus: rentStatusFor(b),
            });
          }
        }

        // Finalise each unit's revenue from its own effective config, so a
        // per-apartment model can't be masked by its property's.
        for (const [key, unit] of unitByKey) {
          unit.monthly_revenue = unitMonthlyRevenue(
            {
              revenue_model: unit.revenue_model,
              coliviq_fee_pct: unit.coliviq_fee_pct,
              coliviq_fee_amount: unit.coliviq_fee_amount,
              master_rent: unit.master_rent,
            },
            occupiedRoomsByUnit.get(key) ?? 0,
            unit.occupied_rent
          );
          unit.revenue_degraded = bookingsFailed;
          unit.rooms_degraded = roomsFailed;
        }
        return Array.from(unassignedByProp.values()).sort((x, y) =>
          (x.property_title ?? "").localeCompare(y.property_title ?? "")
        );
      })();

      // 2. Management invoices by lessor_id — independent of the property
      // enrichment above, so kick it off and await everything together (#8).
      const invoicesEnrichment = (async (): Promise<OwnerInvoice[]> => {
        const { data: invRows, error: invErr } = await supabase
          .from("management_invoices")
          .select(
            "id, invoice_no, period_month, amount_cents, status, due_date, paid_at, proof_url, invoice_pdf_url, fee_basis, metadata"
          )
          .eq("lessor_id", lessor.id)
          .order("period_month", { ascending: false });
        if (invErr) throw invErr;
        return (invRows ?? []).map((r: any) => ({
          ...r,
          proof_note:
            (r.metadata as Record<string, unknown> | null)?.proof_note != null
              ? String((r.metadata as Record<string, unknown>).proof_note)
              : null,
        })) as OwnerInvoice[];
      })();

      const [
        { stage: resolveStage, foreignTokenIds },
        unassignedUnits,
        invoices,
      ] = await Promise.all([
        onboardingEnrichment,
        roomsEnrichment,
        invoicesEnrichment,
      ]);

      // Each unit gets ITS OWN stage: an apartment reads its own invitation (or
      // a property-wide one), never a sibling apartment's. The property-level
      // "unassigned rooms" unit has no apartment id, so it reads the property.
      for (const unit of [...apartments, ...unassignedUnits]) {
        const stage = resolveStage(unit.property_id, unit.id);
        unit.onboarding_stage = stage;
        unit.onboarding_label = ONBOARDING_STAGE_LABEL[stage];
      }

      // 3. Onboarding submission. Match by the owner's email first, then fall
      // back to the submission linked via one of the owner's property onboarding
      // tokens — the SAME link the Owners list uses for its "Onboarded" badge.
      // Without the token fallback, a submission whose email differs from the
      // lessor's (submitted under a different address, or email left blank) makes
      // this tab read "No onboarding submission" while the list shows the owner
      // as onboarded — the exact inconsistency we're fixing.
      const SUB_SELECT =
        "*, token:token_id ( email, property_id, prefilled_fee_amount, prefilled_fee_model )";
      // An owner can onboard more than once (one submission per apartment /
      // property, or a re-submission after a rejection), so collect EVERY
      // attributable submission — the Onboarding tab nests them all.
      const subRowsById = new Map<string, any>();
      const collectSubs = (rows: any[] | null | undefined) => {
        for (const r of rows ?? []) if (r?.id) subRowsById.set(String(r.id), r);
      };
      if (lessor.email) {
        const res = await supabase
          .from("owner_onboarding_submissions")
          .select(SUB_SELECT)
          .ilike("email", lessor.email)
          .order("submitted_at", { ascending: false });
        warnEnrichment("onboarding submissions (by email)", res.error);
        // Wildcard-safe: keep only the rows whose address really is this
        // owner's (see sameOwnerEmail).
        collectSubs(
          (res.data ?? []).filter((r: any) =>
            sameOwnerEmail(r?.email, lessor.email)
          )
        );
      }
      // Fall back to the tokens ATTRIBUTABLE TO THIS OWNER — the same two ways
      // ownerPropertyOnboardingStage attributes them, so this tab and the
      // "Onboarded" badge can never disagree: a token on a property the owner
      // HOLDS, or a token whose invite was addressed to them. A token on a
      // property held by a DIFFERENT lessor (one merely hosting an apartment of
      // this owner) belongs to that lessor's onboarding and is never attributed
      // here — that mismatch is what made the badge read "Onboarded" while this
      // tab said "No onboarding submission on file".
      if (ownedPropIds.length > 0 || lessor.email) {
        const [byProp, byEmail] = await Promise.all([
          ownedPropIds.length > 0
            ? supabase
                .from("owner_onboarding_tokens")
                .select("id")
                .in("property_id", ownedPropIds)
            : Promise.resolve({ data: [], error: null }),
          lessor.email
            ? supabase
                .from("owner_onboarding_tokens")
                // `email` is selected so the wildcard-safe check below can run;
                // an `ilike` pattern alone would pull in a similarly-spelled
                // owner's tokens (see sameOwnerEmail).
                .select("id, email")
                .ilike("email", lessor.email)
            : Promise.resolve({ data: [], error: null }),
        ]);
        warnEnrichment("onboarding submission (token fallback)", byProp.error);
        warnEnrichment("onboarding submission (invite email)", byEmail.error);
        const tokenIds = Array.from(
          new Set(
            [
              // A token on this owner's property, EXCEPT one scoped to an
              // apartment another lessor holds — that is their onboarding, and
              // listing its submission here would put their bank details and
              // signed contracts in this owner's tab (and contradict the stage,
              // which excludes exactly the same tokens).
              ...(byProp.data ?? []).filter(
                (t: any) => !foreignTokenIds.has(String(t.id))
              ),
              ...(byEmail.data ?? []).filter((t: any) =>
                sameOwnerEmail(t?.email, lessor.email)
              ),
            ].map((t: any) => String(t.id))
          )
        );
        if (tokenIds.length > 0) {
          const res = await supabase
            .from("owner_onboarding_submissions")
            .select(SUB_SELECT)
            .in("token_id", tokenIds)
            .order("submitted_at", { ascending: false });
          warnEnrichment("onboarding submissions (by token)", res.error);
          collectSubs(res.data);
        }
      }

      // Newest first — the tab lists them in that order and `submission` (the
      // primary, used by the stats/documents/activity below) is the latest one.
      const subRows = Array.from(subRowsById.values()).sort((a, b) =>
        String(b.submitted_at ?? "").localeCompare(String(a.submitted_at ?? ""))
      );
      // Resolve every submission's property in ONE query — a per-submission
      // lookup would be N round-trips for an owner who onboarded N times.
      const subPropIds = Array.from(
        new Set(
          subRows
            .map((r) => r.token?.property_id)
            .filter((p) => p != null)
            .map(Number)
        )
      );
      const propByIdForSubs = new Map<
        string,
        { title: string | null; address: string | null }
      >();
      if (subPropIds.length > 0) {
        const { data: props, error: propErr } = await supabase
          .from("properties")
          .select("id, title, address, city")
          .in("id", subPropIds);
        warnEnrichment("onboarding submission properties", propErr);
        for (const p of props ?? []) {
          propByIdForSubs.set(String(p.id), {
            title: p.title ?? null,
            address: [p.address, p.city].filter(Boolean).join(", ") || null,
          });
        }
      }
      // Signed contracts for EVERY submission in one query — the old code only
      // fetched the latest submission's, so an owner who onboarded twice lost the
      // earlier set entirely (from this tab AND from the Documents tab).
      const contractsBySubmission = new Map<
        string,
        { name: string; url: string }[]
      >();
      if (subRows.length > 0) {
        const { data: contractRows, error: contractErr } = await supabase
          .from("owner_signed_contracts")
          .select(
            "submission_id, signed_pdf_url, owner_contract_templates(name)"
          )
          .in(
            "submission_id",
            subRows.map((r) => r.id)
          );
        warnEnrichment("onboarding signed contracts", contractErr);
        for (const row of contractRows ?? []) {
          if (!row.signed_pdf_url) continue;
          const key = String(row.submission_id);
          const list = contractsBySubmission.get(key) ?? [];
          list.push({
            name: row.owner_contract_templates?.name || "Management Agreement",
            url: row.signed_pdf_url,
          });
          contractsBySubmission.set(key, list);
        }
      }
      const submissions: OwnerSubmission[] = subRows.map((row) => {
        const prop =
          row.token?.property_id != null
            ? propByIdForSubs.get(String(row.token.property_id))
            : undefined;
        return {
          id: row.id,
          reference_number: row.reference_number,
          submitted_at: row.submitted_at,
          status: row.status,
          rejection_reason: row.rejection_reason,
          address: row.address,
          bank_holder: row.bank_holder,
          iban: row.iban,
          bic: row.bic,
          payment_method: row.payment_method,
          management_agreement_accepted: !!row.management_agreement_accepted,
          verwaltervollmacht_accepted: !!row.verwaltervollmacht_accepted,
          agb_accepted: !!row.agb_accepted,
          withdrawal_policy_accepted: !!row.withdrawal_policy_accepted,
          verified_at: row.verified_at,
          supabase_user_id: row.supabase_user_id,
          propertyTitle: prop?.title ?? null,
          propertyAddress: prop?.address ?? null,
          feeAmount: row.token?.prefilled_fee_amount ?? null,
          feeModel: row.token?.prefilled_fee_model ?? null,
          signedContracts: contractsBySubmission.get(String(row.id)) ?? [],
        };
      });
      const submission: OwnerSubmission | null = submissions[0] ?? null;

      // 4. Owner documents — the three standard owner documents (Management
      // Agreement/AGB, Auftragsbestätigung, Verwaltervollmacht) always show;
      // the owner's SIGNED versions (from their onboarding submission) replace
      // the blank templates when available.
      const signedDocs: { name: string; url: string }[] = [];
      const seen = new Set<string>();
      const pushDoc = (name: string, url: string | null | undefined) => {
        if (!url) return;
        const key = name.trim().toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        signedDocs.push({ name, url });
      };

      // Owner's signed contracts (personalised, from onboarding) — highest
      // priority. Walks EVERY submission newest-first; pushDoc dedupes by name,
      // so the most recent signed version of a document wins while an older
      // submission still contributes anything the newest one lacks.
      if (submission) {
        for (const sub of submissions) {
          for (const doc of sub.signedContracts) pushDoc(doc.name, doc.url);
        }
        // Auftragsbestätigung — generated per submission at a known storage
        // path. A bare contracts path: the Documents tab signs it on click.
        if (submission.reference_number) {
          pushDoc(
            "Auftragsbestätigung",
            `auftragsbestaetigung/${submission.id}_${submission.reference_number}.pdf`
          );
        }
      }

      // Standard document set (blank templates + AGB) — shown for every owner.
      const [{ data: agbDoc }, { data: templates }] = await Promise.all([
        supabase
          .from("platform_documents")
          .select("file_url, title")
          .eq("document_key", "agb")
          .maybeSingle(),
        supabase
          .from("owner_contract_templates")
          .select("name, pdf_url")
          .not("pdf_url", "is", null)
          .order("created_at", { ascending: true }),
      ]);
      for (const tpl of templates ?? []) {
        pushDoc(tpl.name || "Document", tpl.pdf_url);
      }
      pushDoc(agbDoc?.title ?? "AGB Verwaltungsvertrag", agbDoc?.file_url);

      // 5. Manually uploaded owner documents
      const { data: ownerDocRows } = await supabase
        .from("owner_documents")
        .select(
          "id, title, doc_type, file_url, file_name, file_size, notes, created_at"
        )
        .eq("lessor_id", lessor.id)
        .order("created_at", { ascending: false });
      const ownerDocs: OwnerDoc[] = (ownerDocRows ?? []) as OwnerDoc[];

      // 6. SaaS subscription
      const { data: subs } = await supabase
        .from("saas_subscriptions")
        .select("id, plan, status, amount_cents, billing_day, started_at")
        .eq("lessor_id", lessor.id)
        .order("created_at", { ascending: false })
        .limit(1);
      const subscription: OwnerSubscription = subs?.[0] ?? null;

      // 7. Management fee the admin set on the owner's account (profiles.
      // management_fee, € per room per month) — the value entered in the
      // Add/Edit-account "Assign to properties → Management fee" flow. Matched by
      // email like the onboarding submission. Enrichment-degrades: a failure here
      // just leaves the fee unknown, it doesn't break the modal.
      let managementFeePerRoom: number | null = null;
      let managementFeeRooms: number | null = null;
      if (lessor.email) {
        const { data: profileFee, error: profileFeeErr } = await supabase
          .from("profiles")
          .select("management_fee, management_fee_rooms")
          .eq("email", lessor.email)
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        warnEnrichment("owner management fee (profile)", profileFeeErr);
        if (profileFee?.management_fee != null)
          managementFeePerRoom = Number(profileFee.management_fee);
        if (profileFee?.management_fee_rooms != null)
          managementFeeRooms = Number(profileFee.management_fee_rooms);
      }

      // Derived stats — over every unit, so rooms that sit outside an apartment
      // still count toward the owner's rooms and revenue.
      const allUnits = [...apartments, ...unassignedUnits];
      const roomCount = allUnits.reduce((s, u) => s + u.rooms_count, 0);
      const totalMonthlyFees = allUnits.reduce(
        (s, u) => s + u.monthly_revenue,
        0
      );
      const revenueDegraded = allUnits.some((u) => u.revenue_degraded);
      const thisYear = new Date().getFullYear();
      const feesPaidThisYear = invoices
        .filter((i) => {
          if (i.status !== "paid") return false;
          const d = new Date(
            `${String(i.paid_at || i.period_month).slice(0, 10)}T12:00:00`
          );
          return !Number.isNaN(d.getTime()) && d.getFullYear() === thisYear;
        })
        .reduce((s, i) => s + (i.amount_cents || 0) / 100, 0);
      // Since 20260904120000 an owner's month can hold SEVERAL invoices — one
      // per apartment, plus one for unassigned rooms — rather than one
      // consolidated row. `invoices[0]` alone (still the row this month's
      // fee/model LABEL reads off, since fee_basis is a per-unit shape either
      // way) is no longer the owner's monthly fee: it's one arbitrary
      // apartment's fee. currentMonthlyFee must sum every LIVE invoice for
      // that same latest month, or a multi-apartment owner's "Monthly fee"
      // tile understates what they are actually billed.
      const liveInvoices = invoices.filter((i) => i.status !== "canceled");
      const latestInvoice = liveInvoices[0] ?? null;
      const currentMonthlyFee = latestInvoice
        ? liveInvoices
            .filter((i) => i.period_month === latestInvoice.period_month)
            .reduce((s, i) => s + (i.amount_cents || 0), 0) / 100
        : null;

      return {
        apartments,
        unassignedUnits,
        invoices,
        submissions,
        submission,
        signedDocs,
        ownerDocs,
        subscription,
        stats: {
          apartmentCount: apartments.length,
          propertyCount: new Set(allUnits.map((u) => u.property_id)).size,
          roomCount,
          feesPaidThisYear,
          totalMonthlyFees,
          currentMonthlyFee,
          revenueModelLabel: revenueModelLabel(
            latestInvoice?.fee_basis,
            submission?.feeModel,
            submission?.feeAmount
          ),
          rentCollectionLabel: rentCollectionLabel(latestInvoice?.fee_basis),
          managementFeePerRoom,
          managementFeeRooms,
          revenueDegraded,
        },
      };
    },
  });
}

// `owner_documents.file_url` is a bare path in the contracts bucket. The
// Documents tab signs it on click (`<StoredFileLink>`); there is no public
// URL to build any more (COL-436).

/** Upload / delete manual owner documents (admin only, gated by RLS). */
export function useOwnerDocumentMutations(lessor: Lessor | null) {
  const qc = useQueryClient();
  // Both caches, always: the document list AND the audit trail. A delete
  // removes the row the derived "Document uploaded" event came from, so
  // refreshing only the list would make the document — and the record of its
  // removal — disappear from the Activity tab until the next refetch.
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: OWNER_DETAIL_KEY(lessor?.id ?? null) });
    qc.invalidateQueries({ queryKey: OWNER_AUDIT_KEY(lessor?.id ?? null) });
  };

  const upload = useMutation({
    mutationFn: async ({
      file,
      docType,
      ownerUserId,
      notes,
    }: {
      file: File;
      docType: string;
      ownerUserId?: string | null;
      notes?: string | null;
    }) => {
      if (!lessor) throw new Error("no owner");
      // `safe` already includes the original extension — don't append it again.
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `owner-documents/${lessor.id}/${Date.now()}-${safe}`;
      const up = await supabase.storage
        .from(DOC_BUCKET)
        .upload(path, file, { upsert: false });
      if (up.error) throw up.error;

      const { data: auth } = await supabase.auth.getUser();
      const { error } = await supabase.from("owner_documents").insert({
        lessor_id: lessor.id,
        owner_user_id: ownerUserId ?? null,
        title: file.name,
        doc_type: docType,
        file_url: path,
        file_name: file.name,
        file_size: file.size,
        content_type: file.type || null,
        notes: notes?.trim() || null,
        uploaded_by: auth?.user?.id ?? null,
      });
      if (error) {
        await supabase.storage.from(DOC_BUCKET).remove([path]);
        throw error;
      }
      await logOwnerAudit({
        lessorId: lessor.id,
        action: "owner_document_uploaded",
        resourceType: "owner_document",
        resourceId: path,
        newValues: { title: file.name, doc_type: docType },
        metadata: { document_title: file.name, note: notes?.trim() || null },
      });
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (doc: OwnerDoc) => {
      // Delete the authoritative record first; only then best-effort-remove the
      // storage object. If we removed the file first and the row delete failed,
      // the Documents tab would list a doc whose View/Download 404s. An orphaned
      // storage object is harmless; a dangling metadata row is user-visible.
      const { error } = await supabase
        .from("owner_documents")
        .delete()
        .eq("id", doc.id);
      if (error) throw error;
      if (doc.file_url && !/^https?:\/\//i.test(doc.file_url)) {
        await supabase.storage.from(DOC_BUCKET).remove([doc.file_url]);
      }
      // The document row is gone, so this log is the only surviving record that
      // it ever existed — and of who removed it.
      await logOwnerAudit({
        lessorId: lessor?.id,
        action: "owner_document_deleted",
        resourceType: "owner_document",
        resourceId: String(doc.id),
        oldValues: { title: doc.title, doc_type: doc.doc_type },
        metadata: { document_title: doc.title },
      });
    },
    onSuccess: invalidate,
  });

  return { upload, remove };
}

/** One `audit_logs` row as the owner Activity tab reads it. */
export type OwnerAuditRow = {
  id: string;
  action: string;
  resource_type: string;
  resource_id: string | null;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  user_id: string | null;
  /** Hydrated from `profiles` — audit_logs.user_id FKs auth.users, which PostgREST cannot embed. */
  actor: { id: string; name: string | null; email: string | null } | null;
};

export const OWNER_AUDIT_KEY = ownerAuditKey;

/**
 * Every logged action on one owner, newest first.
 *
 * Exported separately from the hook so the error-propagation behavior below
 * is testable without react-query: a failed read THROWS rather than
 * resolving to `[]`, because an empty array here is indistinguishable from
 * "this owner truly has no logged actions" — a real read failure (RLS,
 * transient error) would otherwise silently render as a shorter-but-plausible
 * timeline. The caller (OwnerDetailModal) shows a distinct notice on the
 * query's `isError` and still renders the derived events, which come from a
 * separate query untouched by this failure.
 */
export async function fetchOwnerAuditLogs(
  lessorId: string
): Promise<OwnerAuditRow[]> {
  const { data, error } = await supabase
    .from("audit_logs")
    .select(
      "id, action, resource_type, resource_id, old_values, new_values, metadata, created_at, user_id"
    )
    .eq("metadata->>source", OWNER_AUDIT_SOURCE)
    .eq("metadata->>lessor_id", lessorId)
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw error;
  const rows = (data ?? []) as OwnerAuditRow[];
  const ids = Array.from(
    new Set(rows.map((r) => r.user_id).filter(Boolean))
  ) as string[];
  if (!ids.length) return rows.map((r) => ({ ...r, actor: null }));
  const { data: people } = await supabase
    .from("profiles")
    .select("id, name, email")
    .in("id", ids);
  const byId = new Map(
    (
      (people ?? []) as Array<{
        id: string;
        name: string | null;
        email: string | null;
      }>
    ).map((p) => [p.id, p])
  );
  return rows.map((r) => ({
    ...r,
    actor: r.user_id ? (byId.get(r.user_id) ?? null) : null,
  }));
}

/**
 * Separate from useOwnerDetail rather than folded into it: the trail grows
 * forever while the detail payload is a snapshot, and an action written from
 * the Payments hub has to be able to invalidate this without re-fetching the
 * owner's whole portfolio.
 */
export function useOwnerAuditLogs(lessorId: string | null) {
  return useQuery({
    queryKey: OWNER_AUDIT_KEY(lessorId),
    enabled: !!lessorId,
    queryFn: () => fetchOwnerAuditLogs(lessorId!),
  });
}
